import React from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Link } from 'react-router-dom';
import { TrendingUp, Shield, Activity, BarChart3, Calculator, Wallet, TrendingDown, GitCompare, ArrowRight } from 'lucide-react';

const ResearchHubPage = () => {
  const researchTools = [
    {
      icon: TrendingUp,
      title: 'Scheme Performance',
      description: 'Track historical performance of mutual fund schemes across all AMCs for 1Y, 3Y, 5Y, 10Y periods with CAGR returns.',
      link: '/scheme-performance',
      gradient: 'from-green-500 to-emerald-500',
      features: ['44+ AMCs', '40+ Categories', 'CAGR Returns']
    },
    {
      icon: Shield,
      title: 'Risk Metrics',
      description: 'Analyze risk and risk-adjusted returns with Standard Deviation, Beta, Alpha, Sharpe, Treynor & Sortino ratios.',
      link: '/risk-metrics',
      gradient: 'from-purple-500 to-pink-500',
      features: ['7 Risk Indicators', '3Y Data', 'Risk-adjusted Returns']
    },
    {
      icon: TrendingDown,
      title: 'Most Declining NAV',
      description: 'Identify schemes with significant NAV decline from their peak. Potential buying opportunities or risk warnings.',
      link: '/declining-nav',
      gradient: 'from-red-500 to-orange-500',
      features: ['Peak NAV Analysis', 'Sensex Reference', 'Decline %']
    },
    {
      icon: Activity,
      title: 'NAV Tracker',
      description: 'Track highest, lowest and current NAV of mutual fund schemes to understand price range and entry points.',
      link: '/nav-tracker',
      gradient: 'from-cyan-500 to-blue-500',
      features: ['NAV History', 'Price Range', 'Visual Tracker']
    },
    {
      icon: BarChart3,
      title: 'Scheme Trends',
      description: 'Analyze NAV trends over custom date ranges with Sensex comparison and performance charts.',
      link: '/scheme-trends',
      gradient: 'from-amber-500 to-orange-500',
      features: ['Date Range Selection', 'Sensex Comparison', 'Trend Analysis']
    },
    {
      icon: GitCompare,
      title: 'Scheme Comparison',
      description: 'Compare multiple mutual fund schemes side-by-side on returns, risk metrics, and performance.',
      link: '/scheme-comparison',
      gradient: 'from-indigo-500 to-purple-500',
      features: ['Up to 20 Schemes', 'Side-by-side View', 'Export Report']
    },
    {
      icon: Calculator,
      title: 'Live SIP Analysis',
      description: 'Calculate how your SIP investment would have grown based on historical returns with XIRR calculation.',
      link: '/live-sip-analysis',
      gradient: 'from-blue-500 to-purple-500',
      features: ['Historical Analysis', 'XIRR Calculator', 'Visual Breakdown']
    },
    {
      icon: Wallet,
      title: 'Live SWP Analysis',
      description: 'Analyze Systematic Withdrawal Plan sustainability. See how your corpus would last with regular withdrawals.',
      link: '/live-swp-analysis',
      gradient: 'from-orange-500 to-red-500',
      features: ['Withdrawal Planning', 'Corpus Projection', 'Sustainability Check']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      
      <div className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Research Tools
            </h1>
            <p className="text-lg text-white/70 max-w-3xl mx-auto">
              Comprehensive mutual fund research and analysis tools to help you make informed investment decisions. 
              Analyze scheme performance, risk metrics, NAV trends, and more.
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10 text-center">
              <p className="text-3xl font-bold text-white">44+</p>
              <p className="text-white/60 text-sm">Fund Houses</p>
            </div>
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10 text-center">
              <p className="text-3xl font-bold text-white">40+</p>
              <p className="text-white/60 text-sm">Scheme Categories</p>
            </div>
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10 text-center">
              <p className="text-3xl font-bold text-white">8</p>
              <p className="text-white/60 text-sm">Research Tools</p>
            </div>
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10 text-center">
              <p className="text-3xl font-bold text-white">Daily</p>
              <p className="text-white/60 text-sm">Data Updates</p>
            </div>
          </div>

          {/* Research Tools Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
            {researchTools.map((tool, index) => {
              const Icon = tool.icon;
              return (
                <Link
                  key={index}
                  to={tool.link}
                  className="group bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-white/30 transition-all duration-300 hover:transform hover:scale-[1.02]"
                >
                  <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r ${tool.gradient} rounded-xl mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{tool.title}</h3>
                  <p className="text-white/60 text-sm mb-4 line-clamp-3">{tool.description}</p>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {tool.features.map((feature, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-white/5 rounded-full text-xs text-white/50">
                        {feature}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center text-white text-sm group-hover:translate-x-1 transition-transform">
                    <span>Open Tool</span>
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Featured Sections */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 backdrop-blur-xl rounded-2xl p-8 border border-green-500/30">
              <h3 className="text-2xl font-bold text-white mb-3">Performance Analysis</h3>
              <p className="text-white/70 mb-4">
                Track and compare historical returns of mutual fund schemes. View CAGR returns for 1Y, 3Y, 5Y, 10Y periods across all AMCs.
              </p>
              <Link to="/scheme-performance" className="inline-flex items-center text-green-400 hover:text-green-300">
                <span>Analyze Performance</span>
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
            <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 backdrop-blur-xl rounded-2xl p-8 border border-purple-500/30">
              <h3 className="text-2xl font-bold text-white mb-3">Risk Assessment</h3>
              <p className="text-white/70 mb-4">
                Evaluate fund risk using professional metrics like Sharpe Ratio, Beta, Alpha, and Sortino Ratio for informed decisions.
              </p>
              <Link to="/risk-metrics" className="inline-flex items-center text-purple-400 hover:text-purple-300">
                <span>Assess Risk</span>
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/10 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Need Help Analyzing Mutual Funds?</h2>
            <p className="text-white/70 mb-6 max-w-2xl mx-auto">
              Our expert advisors can help you interpret the data and create an investment strategy tailored to your goals.
            </p>
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-white text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] transition-all duration-300"
            >
              Speak with an Advisor
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ResearchHubPage;
